import React, { useCallback, useEffect, useState } from 'react';
import { Select } from 'semantic-ui-react';
import {
  ATMField,
  ATMGrid,
  ATMHeader,
  ATMInput,
  ATMTextArea,
  ATMSegment,
  ATMSelect,
  ATMForm,
  ATMDivider,
} from 'shared-it-appmod-ui';
import LabelRequired from 'src/components/atoms/label-required/label-required.component';
import FormFlag from 'src/components/molecules/form-flag/form-flag.component';
import FormFlagField from 'src/components/organisms/form-flag-field/form-flag-field.component';
import FormFlagUpload from 'src/components/organisms/form-flag-upload/form-flag-upload.component';
import { DistrictType, FileCategory } from 'src/constants';
import { useDistrictContext } from 'src/contexts/district.context';
import { useRopeTypeContext } from 'src/contexts/rope-type.context';
import { actionTypes } from 'src/ducks/district.duck';
import Lang from 'src/libraries/language';
import { IRequestFormMethods } from 'src/models/request.model';
import { getDistrictStatus } from 'src/selectors/district.selector';
import { useSensitiveCommunitiesContext } from 'src/contexts/sensitive-communities.context';
import { useDroneAreasContext } from 'src/contexts/drone-areas.context';
import { useSensitiveCitiesContext } from 'src/contexts/sensitive-cities.context';
import FlightRequestConstructionLandingZoneForm from '../../flight-request-construction-landing-zone/flight-request-construction-landing-zone-form/flight-request-construction-landing-zone-form.component';
import FlightRequestConstructionStructure from '../../flight-request-construction-structure/flight-request-construction-structure.component';
import FlightRequestFlightArea from '../../flight-request-flight-area/flight-request-flight-area.component';
import styles from '../flight-request-construction.module.scss';

type IProps = IRequestFormMethods;

const FlightRequestConstructionForm: React.FC<IProps> = ({
  control,
  errors,
}) => {
  const { state, actions } = useDistrictContext();
  const { state: ropeTypeState, actions: ropeTypeAction } =
    useRopeTypeContext();

  React.useEffect(() => {
    actions.listGET();
    ropeTypeAction.listGET();
  }, [actions, ropeTypeAction]);

  const status = getDistrictStatus(state, actionTypes.DISTRICT_LIST_FETCH);

  const { state: sCommunitiesState, actions: sCommunitiesActions } = useSensitiveCommunitiesContext();
  const { state: droneAreasState, actions: droneAreasActions } = useDroneAreasContext();
  const { state: sCitiesState, actions: sCitiesActions } = useSensitiveCitiesContext();
  const [sCityPamLead, setSCityPamLead] = useState<string | null>('');
  const [sCommunityPamLead, setSCommunityPamLead] = useState<string | null>('');

  useEffect(() => {
    if (sCommunitiesActions && sCitiesActions && droneAreasActions) {
      sCommunitiesActions.listGET();
      sCitiesActions.listGET();
      droneAreasActions.listGET();
    }
  }, [sCommunitiesActions, sCitiesActions, droneAreasActions]);

  const handleChange = useCallback(
    (value, type) => {
      if (type === 'city') {
        if (!value) {
          setSCityPamLead(null);
        } else {
          const sCity = sCitiesState.list.find((v) => v.sensitiveCity === value);
          setSCityPamLead(sCity.pamLead);
        }
        control.setValue('constructionFlight.sensitiveCity', value);
      } else if (type === 'drone') {
        control.setValue('constructionFlight.droneArea', value);
      } else {
        if (!value) {
          setSCommunityPamLead(null);
        } else {
          const sCommunity = sCommunitiesState.list.find((v) => v.sensitiveCommunity === value);
          setSCommunityPamLead(sCommunity.sensitiveCommunityPamLead);
        }
        control.setValue('constructionFlight.sensitiveCommunity', value);
      }
    },
    [state, control, sCitiesState, sCommunitiesState]
  );

const [sensitiveCustomerFlag, setSensitiveCustomerFlag] = useState(control?.defaultValuesRef?.current?.constructionFlight?.sensitiveFlag || false);
const defaultSCityPamLead = sCitiesState.list.find((v) => v.sensitiveCity === control?.defaultValuesRef?.current?.constructionFlight?.sensitiveCity)?.pamLead;
const defaultSCommunityPamLead = sCommunitiesState.list.find((v) => v.sensitiveCommunity === control?.defaultValuesRef?.current?.constructionFlight?.sensitiveCommunity)?.sensitiveCommunityPamLead;

  return (
    <ATMSegment color="orange" className={styles.wrapper}>
      <ATMHeader>{Lang.TTL_REQUEST_CONSTRUCTION}</ATMHeader>

      <ATMGrid columns={4}>
        <ATMGrid.Column>
          <ATMField
            key={`flight_dept_${state.list.length}`}
            name="constructionFlight.flightDept"
            label={
              <LabelRequired>
                {Lang.LBL_FLIGHT_DISTRICT_DEPARTMENT}
              </LabelRequired>
            }
            as={Select}
            placeholder={Lang.LBL_SELECT}
            control={control}
            options={state.list
              .filter((value) => value.relationship === DistrictType.Company)
              .map((value) => ({
                key: value.key,
                value: value.value,
                text: value.value,
              }))}
            search
            selectOnBlur={false}
            error={errors.constructionFlight?.flightDept}
            onChange={([_, { value }]) => value}
            loading={!state.list.length && status.fetching}
            disabled={!state.list.length && status.fetching}
          />
        </ATMGrid.Column>
        <ATMGrid.Column>
          <ATMField
            name="constructionFlight.purpose"
            label={<LabelRequired>{Lang.LBL_PURPOSE}</LabelRequired>}
            as={ATMInput}
            placeholder={Lang.LBL_ENTER}
            maxLength={200}
            control={control}
            error={errors.constructionFlight?.purpose}
          />
        </ATMGrid.Column>
      </ATMGrid>

      <FlightRequestConstructionStructure
        category="constructionFlight"
        control={control}
        errors={errors}
      />

      <FlightRequestConstructionLandingZoneForm
        category="constructionFlight"
        control={control}
        errors={errors}
      />

      <ATMHeader size="small" className="text-underline">
        {Lang.TTL_ADMIN_FLIGHT_TYPES}
      </ATMHeader>

      <ATMGrid columns={1}>
        <ATMGrid.Column>
          <FormFlagField
            name="constructionFlight.equipmentFlag"
            label={Lang.LBL_LONG_LINE}
            control={control}
            errors={errors}
          >
            <ATMField
              name="constructionFlight.equipmentDescription"
              label={Lang.LBL_DESCRIPTION}
              control={control}
              as={ATMTextArea}
              placeholder={Lang.LBL_ENTER}
              inline
              style={{ width: '50%' }}
              error={errors.constructionFlight?.equipmentDescription}
            />
          </FormFlagField>
        </ATMGrid.Column>
        <ATMGrid.Column>
          <FormFlagField
            name="constructionFlight.poleFlag"
            label={Lang.LBL_POLE_SET}
            control={control}
            errors={errors}
          >
            <ATMField
              name="constructionFlight.poleCount"
              label={Lang.LBL_POLE_COUNT}
              control={control}
              as={ATMInput}
              type="number"
              placeholder={Lang.LBL_ENTER}
              inline
              error={errors.constructionFlight?.poleCount}
            />
          </FormFlagField>
        </ATMGrid.Column>
        <ATMGrid.Column>
          <FormFlagField
            name="constructionFlight.ropeFlag"
            label={Lang.LBL_SOCK_LINE_PULL}
            control={control}
            errors={errors}
          >
            <ATMField
              name="constructionFlight.ropeSize"
              label={Lang.LBL_ROPE_CONDUCTOR_SIZE}
              control={control}
              as={ATMSelect}
              options={ropeTypeState.list.map((value) => ({
                key: value.ropeId,
                value: value.ropeType,
                text: value.ropeType,
              }))}
              onChange={([_, { value }]) => value}
              placeholder={Lang.LBL_ENTER}
              inline
              error={errors.constructionFlight?.ropeSize}
            />
          </FormFlagField>
        </ATMGrid.Column>
        <ATMGrid.Column>
          <FormFlagField
            name="constructionFlight.hecFlag"
            label={Lang.LBL_SHORT_HAUL_HEC}
            control={control}
            errors={errors}
          >
            <ATMField
              name="constructionFlight.crewCount"
              label={Lang.LBL_CREW_PRESENT}
              control={control}
              as={ATMInput}
              type="number"
              placeholder={Lang.LBL_ENTER}
              inline
              error={errors.constructionFlight?.crewCount}
            />
          </FormFlagField>
        </ATMGrid.Column>
        <ATMGrid.Column>
          <FormFlag
            name="constructionFlight.hazmatFlag"
            label={Lang.LBL_FLYING_HAZMAT}
            control={control}
            errors={errors}
          />
        </ATMGrid.Column>
        <ATMGrid.Column>
          <FormFlag
            name="constructionFlight.crewFlag"
            label={Lang.LBL_CREW_MOVEMENT}
            control={control}
            errors={errors}
          />
        </ATMGrid.Column>
        <ATMGrid.Column>
          <FormFlag
            name="constructionFlight.basketFlag"
            label={Lang.LBL_SKID_BASKET}
            control={control}
            errors={errors}
          />
        </ATMGrid.Column>
        <ATMGrid.Column>
          <ATMForm.Field inline>
            <LabelRequired>{Lang.LBL_HEAVIEST_LOAD_WEIGHT} </LabelRequired>
            <ATMField
              name="constructionFlight.loadWeight"
              control={control}
              as={ATMInput}
              type="number"
              placeholder={Lang.LBL_ENTER}
              inline
              error={errors.constructionFlight?.loadWeight}
            />
          </ATMForm.Field>
        </ATMGrid.Column>
      </ATMGrid>

      <ATMHeader size="small" className="text-underline">
        {Lang.LBL_OTHER_INFORMATION}
      </ATMHeader>

      <ATMGrid columns={1}>
        <ATMGrid.Column>
          <FormFlag
            name="constructionFlight.nightFlag"
            label={Lang.LBL_NIGHT_FLIGHT}
            control={control}
            errors={errors}
          />
        </ATMGrid.Column>

        <ATMGrid.Column>
          <FormFlagUpload
            name="constructionFlight.sensitiveFlag"
            listName="constructionFlight.sensitiveFiles"
            label={Lang.LBL_SENSITIVE_CUSTOMER}
            allowedTypes={[FileCategory.SensitiveCustomer]}
            control={control}
            errors={errors}
            setValue={setSensitiveCustomerFlag}
          />
        </ATMGrid.Column>
        {sensitiveCustomerFlag && (
          <ATMGrid columns={4}>
            <ATMGrid.Column>
              <ATMField
                name="constructionFlight.sensitiveCity"
                label={Lang.LBL_SENSITIVE_CITY}
                placeholder={Lang.LBL_SELECT}
                as={ATMSelect}
                control={control}
                options={sCitiesState.list.map((item) => ({
                  key: item.sensitiveCityId,
                  value: item.sensitiveCity,
                  text: item.sensitiveCity,
                }))}
                onChange={([_, { value }]) => {
                  handleChange(value, 'city');
                  return value;
                }}
                selectOnBlur={false}
                error={errors?.constructionFlight?.sensitiveCity}
                loading={!state.list.length && status.fetching}
                disabled={!state.list.length && status.fetching}
                clearable
              />
              {!(sCityPamLead === null) &&
                (sCityPamLead || defaultSCityPamLead) && (
                  <>
                    <div>City PAM Lead:</div>
                    <div>{sCityPamLead || defaultSCityPamLead}</div>
                  </>
                )}
            </ATMGrid.Column>

            <ATMGrid.Column>
              <ATMField
                name="constructionFlight.sensitiveCommunity"
                label={Lang.LBL_SENSITIVE_COMMUNITIES}
                placeholder={Lang.LBL_SELECT}
                as={ATMSelect}
                control={control}
                options={sCommunitiesState.list.map((item) => ({
                  key: item.sensitiveCommunityId,
                  value: item.sensitiveCommunity,
                  text: item.sensitiveCommunity,
                }))}
                onChange={([_, { value }]) => {
                  handleChange(value, 'community');
                  return value;
                }}
                selectOnBlur={false}
                error={errors?.constructionFlight?.sensitiveCommunity}
                loading={!state.list.length && status.fetching}
                disabled={!state.list.length && status.fetching}
                clearable
              />
              {!(sCommunityPamLead === null) &&
                (sCommunityPamLead || defaultSCommunityPamLead) && (
                  <>
                    <div style={{ opacity: '80%' }}>Community PAM Lead:</div>
                    <div>{sCommunityPamLead || defaultSCommunityPamLead}</div>
                  </>
                )}
            </ATMGrid.Column>

            <ATMGrid.Column>
              <ATMField
                name="constructionFlight.droneArea"
                label={Lang.LBL_DRONE_AREA}
                placeholder={Lang.LBL_SELECT}
                as={ATMSelect}
                control={control}
                options={droneAreasState.list.map((item) => ({
                  key: item.droneAreasId,
                  value: item.SensitiveCityLocation,
                  text: item.SensitiveCityLocation,
                }))}
                onChange={([_, { value }]) => {
                  handleChange(value, 'drone');
                  return value;
                }}
                selectOnBlur={false}
                error={errors?.constructionFlight?.droneArea}
                loading={!state.list.length && status.fetching}
                disabled={!state.list.length && status.fetching}
                clearable
              />
            </ATMGrid.Column>
          </ATMGrid>
        )}
        <ATMGrid.Column>
          <FormFlagUpload
            name="constructionFlight.culturalFlag"
            listName="constructionFlight.culturalFiles"
            label={Lang.LBL_CULTURAL_RESTRICTION}
            allowedTypes={[FileCategory.EnvironmentalCulturalRestrictions]}
            control={control}
            errors={errors}
          />
        </ATMGrid.Column>

        <ATMGrid.Column>
          <FormFlag
            name="constructionFlight.landingFlag"
            label={Lang.LBL_REQUEST_HELP_LANDING_ZONE}
            control={control}
            errors={errors}
          />
        </ATMGrid.Column>
      </ATMGrid>

      <FlightRequestFlightArea
        category="constructionFlight"
        control={control}
        errors={errors}
      />
      <ATMDivider />

      <ATMHeader size="small" className="text-underline">
        {Lang.TTL_BRIEF_DESCRIPTION}
      </ATMHeader>

      <ATMField
        name="constructionFlight.comments"
        label={Lang.LBL_DESCRIPTION_COMMENT}
        control={control}
        as={ATMTextArea}
        placeholder={Lang.LBL_ENTER}
      />
    </ATMSegment>
  );
};

export default FlightRequestConstructionForm;
